/**
 * 系統備份 schema(REQ-20260522):
 * 後端 mysqldump → gzip → S3 (MinIO),提供 list / 下載 / 刪除。
 */
import { z } from 'zod';

export const BackupItemSchema = z.object({
  filename: z.string(),
  key: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type BackupItem = z.infer<typeof BackupItemSchema>;

export const BackupCreateResultSchema = BackupItemSchema;
export type BackupCreateResult = BackupItem;

export const BackupListResponseSchema = z.array(BackupItemSchema);
export type BackupListResponse = BackupItem[];

export const BackupDownloadUrlSchema = z.object({
  url: z.string(),
});
export type BackupDownloadUrl = z.infer<typeof BackupDownloadUrlSchema>;

// ─── 附件檔案備份(REQ-20260729003)────────────────────────────
//
// 跟 SQL 備份的差別在**存放位置**:SQL 存在 MinIO,附件備份存在**主機磁碟**。
// 附件本身就在 MinIO,備份回同一個資料卷等於沒備份 —— 磁碟壞掉兩份一起沒。
// 因此附件走 bind mount 出去的主機目錄,與 MinIO 資料卷分離。
//
// 代價:主機檔案沒有 presigned URL,UI 只能列出 / 刪除,取回要走 SSH。
// 一份快照 4 GB 左右,本來也不適合從瀏覽器拉。

/** 一次附件備份快照(主機上的一個資料夾)*/
export const FileBackupItemSchema = z.object({
  /** 資料夾名,例:`20260729T193000Z` */
  name: z.string(),
  /** 檔案數 */
  fileCount: z.number().int().nonnegative(),
  sizeBytes: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type FileBackupItem = z.infer<typeof FileBackupItemSchema>;

/** 附件備份完成的結果(多帶一個「略過幾個」讓失敗不被靜默吞掉)*/
export const FileBackupCreateResultSchema = FileBackupItemSchema.extend({
  /** 讀取失敗而未納入快照的物件數;>0 代表這份快照不完整 */
  skippedCount: z.number().int().nonnegative(),
});
export type FileBackupCreateResult = z.infer<typeof FileBackupCreateResultSchema>;

// ─── 自動備份排程(REQ-20260729003)────────────────────────────

/** 保留期(天)。SQL 與附件分開設定 —— 附件一份 4 GB,留太多天會吃掉主機磁碟 */
export const BackupScheduleSchema = z.object({
  enabled: z.boolean().default(false),
  /** 執行時間(主機時區 Asia/Taipei)*/
  hour: z.number().int().min(0).max(23).default(3),
  minute: z.number().int().min(0).max(59).default(30),
  /** SQL 備份保留天數 */
  sqlRetentionDays: z.number().int().min(1).max(365).default(14),
  /** 附件備份保留天數 */
  fileRetentionDays: z.number().int().min(1).max(90).default(3),
});
export type BackupSchedule = z.infer<typeof BackupScheduleSchema>;

/** GET 回傳:設定值 + 下次預定執行時間(ISO string;未啟用為 null)*/
export const BackupScheduleViewSchema = BackupScheduleSchema.extend({
  nextRunAt: z.string().nullable(),
  /** 上次自動執行的結果摘要(未跑過為 null)*/
  lastRunSummary: z.string().nullable(),
  lastRunAt: z.string().nullable(),
});
export type BackupScheduleView = z.infer<typeof BackupScheduleViewSchema>;
